import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@xxtract/ui';
import { FOCUSRING } from '../focus';
import { formatGetal } from '../format';
import { useWekenoverzicht, type TrendBron } from '../hooks/useWekenoverzicht';
import { BREEDTE_KLASSE } from '../leerkaart';
import { maandCel, maandStaafBreedte, type MaandCel } from '../maand';
import { maandPeriode, maandWeken, weeknummer } from '../week';
import type { EigenWekenoverzicht, WerksoortSleutel } from '../work-quality.api';
import { Laden } from './MeasurementState';
import { PLAKKENDE_KOP, PLAKKENDE_RIJKOP, ScrollableTable } from './ScrollableTable';

export interface MaandOverzichtProps {
  bron: TrendBron;
  /** De gekozen periode, 1 tot en met 4 volledige kalendermaanden; de pagina houdt hem vast. */
  maanden: number;
  onKiesMaanden: (maanden: number) => void;
  /** Het niveau van de grafiekkop: 2 op Mijn feedback, 3 binnen het Zo-ziet-kader (story 9, 11). */
  kopNiveau?: 2 | 3 | 4;
}

const PERIODES = [1, 2, 3, 4] as const;
const WERKSOORTEN: readonly WerksoortSleutel[] = ['myxxtract', 'pca'];
const WERKSOORT_NAAM: Record<WerksoortSleutel, string> = { myxxtract: 'qa.eigen.werksoortMyxxtract', pca: 'qa.eigen.werksoortPca' };

const KOP = 'whitespace-nowrap text-[0.6875rem] font-semibold uppercase tracking-[0.03em] text-[var(--foreground-muted)]';
const CEL = 'px-3 py-2.5 text-[0.8125rem] text-[var(--foreground)]';

/**
 * De tab Maand: per werksoort een grafiek met één liggende staaf "in één keer goed" per week, en een eigen
 * tabel met dezelfde getallen (SPEC-meerweekenoverzicht-feedback CAP-7; SPEC-werkkwaliteit-herontwerp story 9).
 *
 * Eén overzicht voor de hele periode. De rijen komen uit de berekende weken, oudste boven, niet uit het
 * antwoord. Staaf en cel lezen het hoofdcijfer `inEenKeerGoed`; de breedte komt uit het percentage van de
 * dienst en staat nergens als getal. Werksoorten staan nooit in één grafiek of tabel (M4). De grafiek is
 * `aria-hidden`: de tabel eronder is het tekstalternatief. Geen `title` (UX-DR20).
 */
export function MaandOverzicht({ bron, maanden, onKiesMaanden, kopNiveau = 2 }: MaandOverzichtProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  // Eén `nu` voor de rijen en de periodetekst, zodat ze rond middernacht op de 1e niet uiteenlopen.
  const nu = new Date();
  const weken = maandWeken(nu, maanden);
  const periode = maandPeriodeTekst(nu, maanden, t, locale);
  const periodeTekst = periode.charAt(0).toLocaleUpperCase(locale) + periode.slice(1);
  const lezing = useWekenoverzicht(bron, weken);
  const getal = (waarde: number) => formatGetal(waarde, locale);

  function inhoud() {
    if (lezing.status === 'laden') return <Laden />;
    if (lezing.status === 'fout') {
      return (
        <p className="text-[0.8125rem] text-[var(--foreground-muted)]" data-testid="werkkwaliteit-maand-storing" role="status">
          {t('qa.eigen.maandStoring')}
        </p>
      );
    }
    return WERKSOORTEN.map((werksoort) => (
      <section className="flex min-w-0 flex-col gap-3" key={werksoort}>
        <Grafiek kopNiveau={kopNiveau} overzicht={lezing.overzicht} weken={weken} werksoort={werksoort} />
        <Tabel getal={getal} overzicht={lezing.overzicht} periode={periode} weken={weken} werksoort={werksoort} />
      </section>
    ));
  }

  return (
    <div className="flex min-w-0 flex-col gap-4" data-testid="werkkwaliteit-maand">
      <div aria-label={t('qa.eigen.maandPeriodeAria')} className="flex flex-wrap gap-2" role="group">
        {PERIODES.map((n) => (
          <button
            aria-pressed={n === maanden}
            className={`rounded-lg border px-3 py-1.5 text-[0.8125rem] font-medium text-[var(--foreground-heading)] max-sm:min-h-[44px] [@media(pointer:coarse)]:min-h-[44px] ${
              n === maanden
                ? 'border-[var(--primary)] bg-[var(--background-muted)]'
                : 'border-[var(--border)] bg-[var(--background)] hover:bg-[var(--background-muted)]'
            } ${FOCUSRING}`}
            data-testid={`werkkwaliteit-maand-periode-${n}`}
            key={n}
            onClick={() => onKiesMaanden(n)}
            type="button"
          >
            {t('qa.eigen.maandPeriode', { count: n })}
          </button>
        ))}
      </div>
      <p
        aria-live="polite"
        className="text-[0.8125rem] font-medium text-[var(--foreground-heading)]"
        data-testid="werkkwaliteit-maand-periodetekst"
      >
        {periodeTekst}
      </p>
      {inhoud()}
    </div>
  );
}

/**
 * De getoonde maanden in de actieve taal, zoals ze midden in een zin staan: "juli tot en met september 2026",
 * over de jaargrens "november 2026 tot en met januari 2027", bij één maand "september 2026" (besluit 29-10-2026
 * ronde 3 #3). De periodetekst zet er een hoofdletter voor; het bijschrift van de tabel niet. De maanden zijn
 * UTC-middernachten, dus `timeZone: 'UTC'`; anders noemt een browser westelijk van UTC de maand ervoor.
 */
function maandPeriodeTekst(nu: Date, maanden: number, t: TFunction, locale: string): string {
  const { van, tot } = maandPeriode(nu, maanden);
  const metJaar = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const zonderJaar = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' });
  const tekst =
    van.getTime() === tot.getTime()
      ? metJaar.format(tot)
      : t('qa.eigen.maandPeriodeBereik', {
          van: (van.getUTCFullYear() === tot.getUTCFullYear() ? zonderJaar : metJaar).format(van),
          tot: metJaar.format(tot),
        });
  return tekst;
}

/** De zichtbare tekst bij een staaf: "X van Y", of waarom er geen staaf is. */
function useStaafTekst() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  return (cel: MaandCel) => {
    switch (cel.soort) {
      case 'cijfers':
        return t('qa.eigen.maandStaaf', { goed: formatGetal(cel.goed, locale), beoordeeld: formatGetal(cel.beoordeeld, locale) });
      case 'teWeinig':
        return t('qa.eigen.maandStaafTeWeinig');
      case 'nietNagekeken':
        return t('qa.eigen.maandStaafNietNagekeken');
      default:
        return t('qa.eigen.maandStaafGeenGegevens');
    }
  };
}

interface GrafiekProps {
  overzicht: EigenWekenoverzicht;
  weken: readonly string[];
  werksoort: WerksoortSleutel;
  kopNiveau: 2 | 3 | 4;
}

/**
 * Eén werksoort: per week een rij met het weeknummer, de staaf en de tekst ernaast. Alleen tekening
 * (`aria-hidden`): de tabel eronder zegt hetzelfde in tekst.
 */
function Grafiek({ overzicht, weken, werksoort, kopNiveau }: GrafiekProps) {
  const { t } = useTranslation();
  const tekstVan = useStaafTekst();
  const naam = t(WERKSOORT_NAAM[werksoort]);
  const Kop = `h${kopNiveau}` as const;
  const rijen = weken.map((weekKey) => ({ weekKey, nummer: weeknummer(weekKey), cel: maandCel(overzicht, weekKey, werksoort) }));

  return (
    <>
      <Kop className="text-[0.8125rem] font-semibold text-[var(--foreground-heading)]">{t('qa.eigen.maandGrafiekKop', { werksoort: naam })}</Kop>
      <div
        aria-hidden="true"
        className="flex w-full min-w-0 max-w-[720px] flex-col gap-1.5"
        data-testid={`werkkwaliteit-maand-grafiek-${werksoort}`}
      >
        {rijen.map(({ weekKey, nummer, cel }) => {
          const breedte = cel.soort === 'cijfers' ? maandStaafBreedte(cel) : 0;
          return (
            // Drie vaste kolommen: elke staaf heeft dezelfde baan, dus de breedtes zijn per week te vergelijken.
            // Smal scherm: week en tekst op één regel, de baan eronder over de hele breedte; anders blijft er
            // bij "Extra groot" op 375 px maar een paar tientallen pixels baan over.
            <div
              className="grid grid-cols-[4.5rem_minmax(0,1fr)_7.5rem] items-center gap-x-2 gap-y-1 text-[0.8125rem] max-sm:grid-cols-[4.5rem_minmax(0,1fr)]"
              data-testid={`werkkwaliteit-maand-staaf-${werksoort}-${weekKey}`}
              key={weekKey}
            >
              <span className="text-[var(--foreground-muted)]">{t('qa.eigen.maandWeek', { nummer })}</span>
              {cel.soort === 'cijfers' ? (
                <div className="h-4 min-w-0 rounded-[4px] bg-[var(--background-muted)] max-sm:col-span-2 max-sm:row-start-2">
                  {breedte > 0 && (
                    <div className={`h-full rounded-[4px] bg-[var(--chart-neutral)] ${BREEDTE_KLASSE[breedte] ?? ''}`} data-breedte={breedte} />
                  )}
                </div>
              ) : (
                <div className="max-sm:hidden" />
              )}
              <span
                className={`max-sm:col-start-2 max-sm:row-start-1 ${cel.soort === 'cijfers' ? 'font-semibold text-[var(--foreground)]' : 'text-[var(--foreground-muted)]'}`}
              >
                {tekstVan(cel)}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}

interface TabelProps {
  overzicht: EigenWekenoverzicht;
  weken: readonly string[];
  werksoort: WerksoortSleutel;
  /** De periode zoals midden in een zin ("juli tot en met september 2026"), voor het bijschrift. */
  periode: string;
  getal: (waarde: number) => string;
}

/** Eén werksoort, één rij per week: "beoordeeld" en "in één keer goed"; dezelfde getallen als de staven. */
function Tabel({ overzicht, weken, werksoort, periode, getal }: TabelProps) {
  const { t } = useTranslation();
  const naam = t('qa.rapportkaart.maandTabelNaam', { werksoort: t(WERKSOORT_NAAM[werksoort]), periode });

  function cellen(weekKey: string) {
    const cel = maandCel(overzicht, weekKey, werksoort);
    const id = `werkkwaliteit-maand-cel-${weekKey}-${werksoort}`;
    const beoordeeld = cel.soort === 'cijfers' || cel.soort === 'teWeinig' ? cel.beoordeeld : undefined;
    if (beoordeeld === undefined) {
      const sleutel =
        cel.soort === 'nietNagekeken' ? 'qa.eigen.maandCelNietNagekeken' : cel.soort === 'teWeinig' ? 'qa.eigen.maandCelTeWeinig' : 'qa.eigen.maandCelGeenGegevens';
      return (
        <TableCell className={`${CEL} text-[var(--foreground-muted)]`} colSpan={2} data-testid={`${id}-reden`}>
          {t(sleutel)}
        </TableCell>
      );
    }
    return (
      <>
        <TableCell className={CEL} data-testid={`${id}-beoordeeld`}>
          {getal(beoordeeld)}
        </TableCell>
        {cel.soort === 'cijfers' ? (
          <TableCell className={CEL} data-testid={`${id}-goed`}>
            {getal(cel.goed)}
          </TableCell>
        ) : (
          <TableCell className={`${CEL} text-[var(--foreground-muted)]`} data-testid={`${id}-goed`}>
            {t('qa.eigen.maandCelTeWeinig')}
          </TableCell>
        )}
      </>
    );
  }

  return (
    <ScrollableTable naam={naam}>
      <Table data-testid={`werkkwaliteit-maand-tabel-${werksoort}`}>
        <TableCaption className="sr-only">{naam}</TableCaption>
        <TableHeader>
          <TableRow className="bg-[var(--background-muted)] hover:bg-[var(--background-muted)]">
            <TableHead className={`${KOP} ${PLAKKENDE_KOP}`} scope="col">
              {t('qa.eigen.maandKolomWeek')}
            </TableHead>
            <TableHead className={KOP} scope="col">
              {t('qa.eigen.maandKolomBeoordeeld')}
            </TableHead>
            <TableHead className={KOP} scope="col">
              {t('qa.eigen.maandKolomGoed')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {weken.map((weekKey) => (
            <TableRow className="group" data-testid={`werkkwaliteit-maand-rij-${werksoort}-${weekKey}`} key={weekKey}>
              <TableHead className={`${CEL} whitespace-nowrap font-normal ${PLAKKENDE_RIJKOP}`} scope="row">
                {t('qa.eigen.maandWeek', { nummer: weeknummer(weekKey) })}
              </TableHead>
              {cellen(weekKey)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ScrollableTable>
  );
}
